import { useState, useEffect, useRef } from 'react';
import { Box, IconButton, MenuItem, Select, Typography } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import CloseIcon from '@mui/icons-material/Close';
import { microphoneSelectorStyles } from './styles';

interface MicrophoneSelectorProps {
  onDeviceChange: (deviceId: string) => void;
  selectedDeviceId: string | null;
}

const STORAGE_KEY = 'preferred_microphone_device_id';

export const MicrophoneSelector: React.FC<MicrophoneSelectorProps> = ({
  onDeviceChange,
  selectedDeviceId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [internalSelectedId, setInternalSelectedId] = useState<string>('');
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    const loadDevices = async () => {
      try {
        // Enumerate devices without requesting permission (labels available after recording starts)
        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = allDevices.filter(d => d.kind === 'audioinput');

        // Filter out 'default' devices to avoid duplicates
        const uniqueDevices = audioInputs.filter(
          device => device.deviceId !== 'default'
        );

        // Sort devices to prioritize built-in microphones at the top
        const sortedDevices = uniqueDevices.sort((a, b) => {
          // Exclude external devices (iPhone, iPad, USB, etc.)
          const isExternalA = /iphone|ipad|usb|external|bluetooth/i.test(
            a.label
          );
          const isExternalB = /iphone|ipad|usb|external|bluetooth/i.test(
            b.label
          );

          // Check for built-in indicators - prioritize "(Built-in)" in label
          const isBuiltInA =
            /\(built-in\)|internal/i.test(a.label) && !isExternalA;
          const isBuiltInB =
            /\(built-in\)|internal/i.test(b.label) && !isExternalB;

          if (isBuiltInA && !isBuiltInB) return -1;
          if (!isBuiltInA && isBuiltInB) return 1;
          return 0;
        });

        setDevices(sortedDevices);
        setIsLoading(false);

        // Initialize device selection on first load
        if (
          !hasInitializedRef.current &&
          sortedDevices.length > 0 &&
          sortedDevices[0]
        ) {
          let deviceToSelect = sortedDevices[0].deviceId;

          // Try to restore from localStorage
          try {
            const storedDeviceId = localStorage.getItem(STORAGE_KEY);
            if (
              storedDeviceId &&
              sortedDevices.some(d => d.deviceId === storedDeviceId)
            ) {
              deviceToSelect = storedDeviceId;
            }
          } catch (err) {
            console.warn(
              'Failed to load preferred microphone from localStorage:',
              err
            );
          }

          setInternalSelectedId(deviceToSelect);
          onDeviceChange(deviceToSelect);
          hasInitializedRef.current = true;
        }
      } catch (err) {
        console.error('Failed to enumerate devices:', err);
        setIsLoading(false);
      }
    };

    loadDevices();

    navigator.mediaDevices.addEventListener('devicechange', loadDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', loadDevices);
    };
  }, [onDeviceChange]);

  // Sync with parent if it changes externally
  useEffect(() => {
    if (
      selectedDeviceId &&
      devices.some(d => d.deviceId === selectedDeviceId)
    ) {
      setInternalSelectedId(selectedDeviceId);
    }
  }, [selectedDeviceId, devices]);

  // Use internal state or fallback to first device
  const currentValue = internalSelectedId || (devices[0]?.deviceId ?? '');

  if (!isOpen) {
    return (
      <Box
        sx={microphoneSelectorStyles.toggleButton}
        onClick={() => setIsOpen(true)}
      >
        <MicIcon sx={{ fontSize: 18, mr: 0.5 }} />
        <Typography variant="caption">Select Microphone</Typography>
      </Box>
    );
  }

  return (
    <Box sx={microphoneSelectorStyles.container}>
      <Box sx={microphoneSelectorStyles.header}>
        <Typography variant="body2" sx={microphoneSelectorStyles.title}>
          Microphone
        </Typography>
        <IconButton
          size="small"
          onClick={() => setIsOpen(false)}
          sx={microphoneSelectorStyles.closeButton}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {isLoading ? (
        <Box sx={{ padding: '10px', textAlign: 'center' }}>
          <Typography variant="caption">Loading devices...</Typography>
        </Box>
      ) : devices.length === 0 ? (
        <Box sx={{ padding: '10px', textAlign: 'center' }}>
          <Typography variant="caption">No microphones found</Typography>
        </Box>
      ) : (
        <Select
          value={currentValue}
          onChange={e => {
            const newValue = e.target.value;
            setInternalSelectedId(newValue);
            onDeviceChange(newValue);

            // Save to localStorage
            try {
              localStorage.setItem(STORAGE_KEY, newValue);
            } catch (err) {
              console.warn(
                'Failed to save preferred microphone to localStorage:',
                err
              );
            }
          }}
          size="small"
          fullWidth
          sx={microphoneSelectorStyles.select}
          renderValue={value => {
            const device = devices.find(d => d.deviceId === value);
            return (
              device?.label ||
              `Microphone ${devices.findIndex(d => d.deviceId === value) + 1}`
            );
          }}
        >
          {devices.map(device => (
            <MenuItem key={device.deviceId} value={device.deviceId}>
              {device.label || `Microphone ${devices.indexOf(device) + 1}`}
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  );
};
